import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material"
import Joi from "joi"
import React, { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { FeatureThingTypeMrn } from "shared/featureData"
// Using the super admin specific API with tag invalidation
import { useCreateFeatureMutation } from "src/features/things/thingsSaApi"

// Feature create form input aligned with backend validator (featureValidators.ts)
interface FeatureInput {
  name: string
  refName: string
  description?: string
  defaultActivated: boolean
  category?: string
}

// Mirrors validateFeatureToCreate rules (max lengths & letter-only refName)
const schema = Joi.object<FeatureInput>({
  name: Joi.string().required().max(100),
  description: Joi.string().allow("").max(1000).optional(),
  defaultActivated: Joi.boolean().optional(),
  category: Joi.string().allow("").max(100).optional(),
  refName: Joi.string()
    .required()
    .regex(
      /^[A-Za-z][A-Za-z0-9]*$/,
      "must start with a letter, then letters or numbers",
    )
    .max(100),
})

export const FeatureForm: FC<{
  onSuccess?: () => void
  onCancel?: () => void
}> = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation()

  const [form, setForm] = useState<FeatureInput>({
    name: "",
    refName: "",
    description: "",
    category: "",
    defaultActivated: false,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const [createFeature, { isLoading }] = useCreateFeatureMutation()

  const validate = useCallback(() => {
    const { error } = schema.validate(form, { abortEarly: false })
    if (!error) {
      setErrors({})
      return true
    }
    const e: Record<string, string> = {}
    error.details.forEach((d) => {
      const key = d.path[0] as string
      if (!e[key]) e[key] = d.message
    })
    setErrors(e)
    return false
  }, [form])

  const handleChange =
    (field: keyof FeatureInput) =>
    (evt: React.ChangeEvent<HTMLInputElement>) => {
      const value = evt.target.value
      if (field === "refName") {
        // Allow first char letter, subsequent alphanumeric
        let cleaned = value.replace(/[^A-Za-z0-9]/g, "")
        // Ensure first character is a letter; strip leading digits
        cleaned = cleaned.replace(/^[^A-Za-z]+/, "")
        setForm((f) => ({ ...f, refName: cleaned }))
        return
      }
      setForm((f) => ({ ...f, [field]: value }))
    }

  const handleCheckbox =
    (field: keyof FeatureInput) =>
    (evt: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setForm((f) => ({ ...f, [field]: checked }))
    }

  const submit = async (evt: React.FormEvent) => {
    evt.preventDefault()
    if (!validate()) return
    try {
      const { name, refName, description, defaultActivated, category } = form
      const attrs: any = {
        name,
        refName,
        defaultActivated,
      }
      if (description !== undefined) attrs.description = description
      if (category) attrs.category = category

      const thing = {
        thingTypeMrn: FeatureThingTypeMrn,
        ...attrs,
      } as any
      await createFeature({ body: { things: [thing] } }).unwrap()
      setSnackbar({
        open: true,
        message: t("feature.created"),
        severity: "success",
      })
      onSuccess?.()
      setForm({
        name: "",
        refName: "",
        description: "",
        category: "",
        defaultActivated: false,
      })
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.message || err?.data?.cause || "Error",
        severity: "error",
      })
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack direction="column" spacing={2} sx={{ mt: 1, width: 400 }}>
        <TextField
          label={t("feature.name")}
          value={form.name}
          onChange={handleChange("name")}
          error={!!errors.name}
          helperText={errors.name}
          size="small"
          required
          fullWidth
        />
        <Tooltip title={t("feature.refName.tooltip") as string}>
          <TextField
            label={t("feature.refName")}
            value={form.refName}
            onChange={handleChange("refName")}
            error={!!errors.refName}
            helperText={errors.refName || t("feature.refName.tooltip")}
            size="small"
            required
            fullWidth
            inputProps={{ pattern: "[A-Za-z]+" }}
          />
        </Tooltip>
        <TextField
          label={t("feature.description")}
          value={form.description}
          onChange={handleChange("description")}
          error={!!errors.description}
          helperText={errors.description}
          size="small"
          fullWidth
          multiline
          minRows={3}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>{t("feature.category")}</InputLabel>
          <Select
            value={form.category || ""}
            label={t("feature.category")}
            onChange={(e) =>
              setForm((f) => ({ ...f, category: e.target.value as string }))
            }
          >
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
            <MenuItem value="Reservation">
              {t("feature.category.Reservation")}
            </MenuItem>
            <MenuItem value="Locker">{t("feature.category.Locker")}</MenuItem>
            <MenuItem value="Other">{t("feature.category.Other")}</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.defaultActivated}
              onChange={handleCheckbox("defaultActivated")}
              size="small"
            />
          }
          label={t("feature.defaultActivated")}
        />
        <Stack direction="row" spacing={2} justifyContent="flex-end" mt={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            size="small"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? t("processing") : t("create")}
          </Button>
        </Stack>
      </Stack>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </form>
  )
}

export default FeatureForm
